import React from 'react';
import { Link as GatsbyLink } from 'gatsby';
import {
    Tab,
    Tabs,
    TabList,
    TabPanel,
    TabPanels,
    Flex,
    Box,
    Heading,
    Link
} from '@chakra-ui/react'
import { PrismicRichText } from '@prismicio/react';
import { BsFileEarmarkPdf } from 'react-icons/bs';
import { StaticImage } from 'gatsby-plugin-image';
import { CustomLink } from '../../utils/customLink';
import { MdOutlineOndemandVideo } from 'react-icons/md';

// The special meeting fields only exist on the City Council page's version of this
// slice, so every read has to tolerate them being absent.
const hasContent = (field) => Boolean(field?.richText?.length)

const MeetingTabs = ({ slice }) => {
    const {
        show_special_meeting_tab: showSpecialTab,
        special_meeting_tab_title: specialTabTitle,
        special_meeting_date: specialDate,
        special_meeting_agenda_header: specialAgendaHeader,
        special_meeting_agenda_packet: specialAgendaPacket,
        special_meeting_instructions: specialInstructions,
        link_to_special_meeting_packet_pdf: specialPacketPdf,
        special_meeting_zoom_link: specialZoomLink,
    } = slice.primary

    const hasSpecialContent = Boolean(
        specialTabTitle ||
        specialDate ||
        specialPacketPdf?.url ||
        hasContent(specialAgendaHeader) ||
        hasContent(specialAgendaPacket)
    )

    // The toggle wins when an editor has set it. Page types without the field fall
    // back to showing the tab whenever it has something in it.
    const hasSpecialMeeting =
        typeof showSpecialTab === 'boolean' ? showSpecialTab : hasSpecialContent

    const specialTabLabel =
        specialTabTitle || (specialDate ? `Special Meeting ${specialDate}` : 'Special Meetings')

    return (
        <Tabs isFitted variant='enclosed-colored' my="12">
            <TabList mb='1em'>
                <Tab fontSize="1.125rem" fontWeight="700">Upcoming Meeting {slice.primary.upcoming_meeting_date}</Tab>
                {hasSpecialMeeting && (
                    <Tab fontSize="1.125rem" fontWeight="700">{specialTabLabel}</Tab>
                )}
                <Tab fontSize="1.125rem" fontWeight="700">Previous Meeting {slice.primary.previous_meeting_date}</Tab>
            </TabList>
            <TabPanels>
                <TabPanel>
                    <Flex w="100%">
                        <Box width="100%">
                            <Flex w="100%" justifyContent="space-between" mb="4" flexWrap="wrap">
                                <Box>
                                    <Heading as="h3" fontSize="1.5rem" textTransform="uppercase">
                                        Meeting Agenda Packet
                                    </Heading>
                                </Box>
                                <Box>
                                    <Link 
                                        href={slice.primary.link_to_upcoming_meeting_packet_pdf.url} 
                                        fontWeight="700"
                                        fontSize="1.25rem"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        >
                                            Download Agenda Packet PDF <span><BsFileEarmarkPdf style={{display: 'inline-block', verticalAlign: 'middle'}}/></span>
                                    </Link>
                                </Box>
                                <Box>
                                <Link 
                                    href={slice.primary.zoom_meeting_link?.text} 
                                    fontWeight="700"
                                    fontSize="1.25rem"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Watch Meeting Live On Zoom <span><MdOutlineOndemandVideo style={{display: 'inline-block', verticalAlign: 'middle'}}/></span>
                                </Link>
                            {/* <Heading as="h3" fontSize="1.5rem" textTransform="uppercase">
                                Watch Meeting Live On Vimeo <MdOutlineOndemandVideo />
                            </Heading>
                            <Box>
                                <a href={slice.primary.zoom_meeting_link?.text} target="_blank" rel="noopener noreferrer">
                                    <StaticImage src="../../images/zoomLogo.svg" alt="Zoom Meeting Link" placeholder="blurred"/>
                                </a>
                            </Box> */}
                        </Box>
                            </Flex>
                            <Box>
                                <PrismicRichText 
    field={slice.primary.agenda_header?.richText} components={{
      hyperlink: ({children, node}) => (
        <CustomLink node={node}>{children}</CustomLink>
      ),
    }} />
                            </Box>
                            <Box>
                                <PrismicRichText 
    field={slice.primary.upcoming_meeting_agenda_packet?.richText} components={{
      hyperlink: ({children, node}) => (
        <CustomLink node={node}>{children}</CustomLink>
      ),
    }}/>
                            </Box>
                            <Box>
                                <PrismicRichText 
    field={slice.primary.instructions?.richText} components={{
      hyperlink: ({children, node}) => (
        <CustomLink node={node}>{children}</CustomLink>
      ),
    }} />
                            </Box> 
                            
                        </Box>
                       
                    </Flex>
                </TabPanel>
                {hasSpecialMeeting && (
                    <TabPanel>
                        <Flex w="100%">
                            <Box width="100%">
                                <Flex w="100%" justifyContent="space-between" mb="4" flexWrap="wrap">
                                    <Box>
                                        <Heading as="h3" fontSize="1.5rem" textTransform="uppercase">
                                            Special Meeting Agenda Packet
                                        </Heading>
                                    </Box>
                                    {specialPacketPdf?.url && (
                                        <Box>
                                            <Link
                                                href={specialPacketPdf.url}
                                                fontWeight="700"
                                                fontSize="1.25rem"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Download Agenda Packet PDF <span><BsFileEarmarkPdf style={{display: 'inline-block', verticalAlign: 'middle'}}/></span>
                                            </Link>
                                        </Box>
                                    )}
                                    {specialZoomLink?.text && (
                                        <Box>
                                            <Link
                                                href={specialZoomLink.text}
                                                fontWeight="700"
                                                fontSize="1.25rem"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Watch Meeting Live On Zoom <span><MdOutlineOndemandVideo style={{display: 'inline-block', verticalAlign: 'middle'}}/></span>
                                            </Link>
                                        </Box>
                                    )}
                                </Flex>
                                <Box>
                                    <PrismicRichText
    field={specialAgendaHeader?.richText} components={{
      hyperlink: ({children, node}) => (
        <CustomLink node={node}>{children}</CustomLink>
      ),
    }} />
                                </Box>
                                <Box>
                                    <PrismicRichText
    field={specialAgendaPacket?.richText} components={{
      hyperlink: ({children, node}) => (
        <CustomLink node={node}>{children}</CustomLink>
      ),
    }}/>
                                </Box>
                                <Box>
                                    <PrismicRichText
    field={specialInstructions?.richText} components={{
      hyperlink: ({children, node}) => (
        <CustomLink node={node}>{children}</CustomLink>
      ),
    }} />
                                </Box>
                            </Box>
                        </Flex>
                    </TabPanel>
                )}
                <TabPanel>
                    <Flex w="100%" flexWrap="wrap">
                        <Box width={{base: '100%', lg: '50%'}} pr="8">
                                <Box mb="4">
                                    <Link 
                                        href={slice.primary.link_to_previous_meeting_packet_pdf?.url} 
                                        fontWeight="700"
                                        fontSize="1.25rem"
                                        target="_blank"
                                        rel="noopener noreferrer">
                                            {slice.primary.previous_meeting_date} Agenda Packet PDF <span><BsFileEarmarkPdf style={{display: 'inline-block', verticalAlign: 'middle'}}/></span>
                                    </Link>
                                </Box>
                                <Box>
                                    <PrismicRichText 
    field={slice.primary.archive?.richText} components={{
      hyperlink: ({children, node}) => (
        <CustomLink node={node}>{children}</CustomLink>
      ),
    }}/>
                                </Box>
                        </Box>
                        <Box width={{base: '100%', lg: '50%'}}>
                            <Heading as="h3" fontSize="1.5rem" textTransform="uppercase">
                                Watch Previous Meeting
                            </Heading>
                            <Box>
                                <Box dangerouslySetInnerHTML={{ __html: slice.primary.vimeo_meeting_iframe_embed_code.richText[0]?.text }} />
                            </Box>
                        </Box>
                    </Flex>
                </TabPanel>
            </TabPanels>
        </Tabs>
    )
};

export default MeetingTabs;